import type { TplHistory, TplMonth, TplProvider } from "@/modules/fulfilment_costs/api";
import { COMPANY_LABEL } from "@/modules/fulfilment_costs/TplHistoryTab";

export const CURRENT_3PLS = ["jj_uk", "omnipack", "jj_us", "tfi"] as const;
export type ProviderId = (typeof CURRENT_3PLS)[number];
export type Part = "Subscription" | "Storage" | "D2C shipping" | "Amazon inbound" | "Stock freight" | "Other";
export const PARTS: readonly Part[] = ["Subscription", "Storage", "D2C shipping", "Amazon inbound", "Stock freight", "Other"];
export const D2C_TYPES: readonly string[] = ["pick_pack", "packaging", "courier", "amendments", "fulfilment_unsplit"];
/** Engine flag codes for a month whose bill detail does not tie to its total (the split by part may be off; the total is the bill). */
export const SPLIT_SUSPECT_FLAGS: readonly string[] = ["invoice_gap", "residual_negative"];
export const HISTORY_FROM = "2022-01";
export const MAX_MONTHS = 72;
export const WINDOW = 12;
export const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
export type IndexMetric = "total" | "per_parcel" | "d2c" | "storage";
export const INDEX_METRICS: ReadonlyArray<{ value: IndexMetric; label: string }> = [
  { value: "total", label: "Total cost" },
  { value: "per_parcel", label: "Cost per parcel" },
  { value: "d2c", label: "D2C per customer parcel" },
  { value: "storage", label: "Storage per month" },
];
export const INDEX_FLOOR = 50;

/** "a, b,,a" -> ["a", "b"]: comma list from the URL, trimmed, deduplicated, empties dropped. */
export function parseList(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  return [...new Set(raw.split(",").map((value) => value.trim()).filter(Boolean))];
}

/** PFC's rule (not inactive), narrowed by company and region (empty = all); current 3PLs first, then by label. */
export function selectableProviders(providers: TplProvider[], companies: string[], regions: string[]): TplProvider[] {
  const rank = (provider: TplProvider) => {
    const index = (CURRENT_3PLS as readonly string[]).indexOf(provider.provider_id);
    return index < 0 ? CURRENT_3PLS.length : index;
  };
  return providers
    .filter((provider) => provider.status !== "inactive"
      && (companies.length === 0 || companies.includes(provider.entity_id))
      && (regions.length === 0 || regions.includes(provider.region)))
    .sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
}

/** Company choices carried by served, active providers, optionally narrowed by region. */
export function companyOptions(providers: TplProvider[], regions: string[]): string[] {
  const live = providers.filter((provider) => provider.status !== "inactive"
    && (regions.length === 0 || regions.includes(provider.region)));
  return Object.keys(COMPANY_LABEL).filter((company) => live.some((provider) => provider.entity_id === company));
}

/** Region choices carried by served, active providers, optionally narrowed by company. */
export function regionOptions(providers: TplProvider[], companies: string[]): string[] {
  const live = providers.filter((provider) => provider.status !== "inactive"
    && (companies.length === 0 || companies.includes(provider.entity_id)));
  return ["UK", "EU", "MENA", "US"].filter((region) => live.some((provider) => provider.region === region));
}

/** Picked ids that are still options, in option order; none -> the four current 3PLs when unfiltered, else every option. */
export function chosenIds(options: TplProvider[], picked: string[], filtered: boolean): string[] {
  const kept = options.map((provider) => provider.provider_id).filter((id) => picked.includes(id));
  if (kept.length > 0) return kept;
  return filtered ? options.map((provider) => provider.provider_id) : [...CURRENT_3PLS];
}

/** The 4 parts agreed 20 Oct, plus Stock freight and Other. J&J US has no shop: its courier lines are stock freight (DEC-1106). */
export function partOf(providerId: string, costType: string): Part | null {
  if (costType === "excluded") return null;
  if (costType === "subscription") return "Subscription";
  if (costType === "storage") return "Storage";
  if (costType === "amazon_reshipping") return "Amazon inbound";
  if (costType === "stock_freight") return "Stock freight";
  if (providerId === "jj_us" && costType === "courier") return "Stock freight";
  if (D2C_TYPES.includes(costType)) return "D2C shipping";
  return "Other";
}

export const ym = (month: string): string => month.slice(0, 7);

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  const t = y * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

export function shortMonth(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(2, 4)}`;
}

/** Write a chronological list of YYYY-MM values once, in words. */
export function monthsInWords(months: string[]): string {
  const ordered = [...new Set(months.map(ym))].sort();
  if (ordered.length === 0) return "";

  const parts = ordered.map((month) => ({
    month: MONTH_NAMES[Number(month.slice(5, 7)) - 1],
    year: month.slice(0, 4),
    serial: Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1,
  }));
  const consecutive = parts.every((part, index) => index === 0 || part.serial === parts[index - 1].serial + 1);
  if (consecutive && parts.length > 1) {
    const first = parts[0];
    const last = parts.at(-1)!;
    return `${first.month}${first.year === last.year ? "" : ` ${first.year}`} to ${last.month} ${last.year}`;
  }

  const words = parts.map((part, index) => {
    const next = parts[index + 1];
    return `${part.month}${!next || next.year !== part.year ? ` ${part.year}` : ""}`;
  });
  if (words.length === 1) return words[0];
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

export function lastCompleteMonth(today: Date): string {
  return addMonths(`${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, "0")}`, -1);
}

export function fetchFrom(fetchTo: string): string {
  const floor = addMonths(fetchTo, -(MAX_MONTHS - 1));
  return floor > HISTORY_FROM ? floor : HISTORY_FROM;
}

export function monthsBetween(from: string, to: string): number {
  const [fromYear, fromMonth] = from.split("-").map(Number);
  const [toYear, toMonth] = to.split("-").map(Number);
  return (toYear - fromYear) * 12 + toMonth - fromMonth + 1;
}

export function validTo(raw: unknown, fetchTo: string): string | null {
  if (typeof raw !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) return null;
  return raw >= fetchFrom(fetchTo) && raw <= fetchTo ? raw : null;
}

export function validFrom(raw: unknown, to: string, fetchTo: string): string | null {
  if (typeof raw !== "string" || !/^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) return null;
  return raw >= fetchFrom(fetchTo) && raw <= to ? raw : null;
}

export function defaultFrom(to: string, fetchTo: string): string {
  const candidate = addMonths(to, -(WINDOW - 1));
  const loadedFrom = fetchFrom(fetchTo);
  return candidate > loadedFrom ? candidate : loadedFrom;
}

export function inWindow(month: TplMonth, to: string, n: number): boolean {
  const key = ym(month.month);
  return key >= addMonths(to, -(n - 1)) && key <= to;
}

export function emptyParts(): Record<Part, number> {
  return { Subscription: 0, Storage: 0, "D2C shipping": 0, "Amazon inbound": 0, "Stock freight": 0, Other: 0 };
}

export function monthParts(providerId: string, month: TplMonth): Record<Part, number> {
  const parts = emptyParts();
  for (const cost of month.costs) {
    const part = partOf(providerId, cost.cost_type);
    if (part) parts[part] += Number(cost.amount);
  }
  return parts;
}

const sumParts = (parts: Record<Part, number>): number => PARTS.reduce((acc, part) => acc + parts[part], 0);
export const isBilled = (providerId: string, month: TplMonth): boolean => Math.abs(sumParts(monthParts(providerId, month))) >= 0.005;
const nonFba = (month: TplMonth): number => month.parcels.total - month.parcels.fba;
const div = (a: number, b: number): number | null => (b === 0 ? null : a / b);
const change = (current: number | null, previous: number | null): number | null =>
  current == null || previous == null || previous === 0 ? null : current / previous - 1;
const amountOf = (month: TplMonth, costType: string): number =>
  month.costs.filter((c) => c.cost_type === costType).reduce((acc, c) => acc + Number(c.amount), 0);
const hasLine = (month: TplMonth, costType: string): boolean => month.costs.some((c) => c.cost_type === costType);
const isSuspect = (month: TplMonth): boolean => month.flags.some((f) => SPLIT_SUSPECT_FLAGS.includes(f.code));

/** The selected GBP metric for one billed provider-month. */
export function metricValue(providerId: string, month: TplMonth, metric: IndexMetric): number | null {
  if (!isBilled(providerId, month)) return null;
  const parts = monthParts(providerId, month);
  const total = sumParts(parts);
  if (metric === "total") return total;
  if (metric === "per_parcel") return div(total, month.parcels.total);
  if (metric === "d2c") return div(parts["D2C shipping"], nonFba(month));
  return hasLine(month, "storage") ? amountOf(month, "storage") : null;
}

export interface IndexRow { month: string; [key: string]: number | string | null }

/** One row per month; each provider's first billed month with parcels is its base of 100. */
export function indexRows(histories: TplHistory[], from: string, to: string, metric: IndexMetric): IndexRow[] {
  const rows: IndexRow[] = Array.from({ length: monthsBetween(from, to) }, (_, index) => ({
    month: addMonths(from, index),
  }));
  for (const history of histories) {
    const id = history.provider.provider_id;
    const baseMonth = history.months
      .filter((month) => {
        const key = ym(month.month);
        return key >= from && key <= to && isBilled(id, month) && month.parcels.total > 0;
      })
      .sort((a, b) => ym(a.month).localeCompare(ym(b.month)))[0];
    const baseCandidate = baseMonth ? metricValue(id, baseMonth, metric) : null;
    const base = baseCandidate != null && Number.isFinite(baseCandidate) && baseCandidate !== 0
      ? baseCandidate
      : null;
    for (const row of rows) {
      const served = history.months.find((month) => ym(month.month) === row.month);
      const candidate = served ? metricValue(id, served, metric) : null;
      const value = candidate != null && Number.isFinite(candidate) ? candidate : null;
      row[`${id}_v`] = value;
      row[id] = base == null || baseMonth == null || row.month < ym(baseMonth.month) || value == null
        ? null
        : Math.round((value / base) * 1000) / 10;
    }
  }
  return rows;
}

/** Provider ids with no usable base month in the selected range. */
export function indexGaps(histories: TplHistory[], from: string, to: string, metric: IndexMetric): string[] {
  const rows = indexRows(histories, from, to, metric);
  return histories.map((history) => history.provider.provider_id)
    .filter((id) => rows.every((row) => row[id] == null));
}

export function defaultTo(histories: TplHistory[], fetchTo: string): string {
  const latest = histories
    .map((h) => h.months.filter((m) => ym(m.month) <= fetchTo && isBilled(h.provider.provider_id, m)).map((m) => ym(m.month)).sort().pop())
    .filter((m): m is string => m != null);
  if (latest.length === 0) return fetchTo;
  const earliest = latest.sort()[0];
  const loadedFrom = fetchFrom(fetchTo);
  return earliest < loadedFrom ? loadedFrom : earliest;
}

export function lastBilled(history: TplHistory, to: string): string | null {
  const id = history.provider.provider_id;
  return history.months.filter((m) => ym(m.month) <= to && isBilled(id, m)).map((m) => ym(m.month)).sort().pop() ?? null;
}

export interface RunRate {
  providerId: string;
  monthsBilled: number;
  parts: Record<Part, number>;
  total: number;
  yearly: number | null;
  yearlyParts: Record<Part, number> | null;
  fixed: number;
  variable: number;
  other: number;
  fixedShare: number | null;
  fixedPerMonth: number | null;
  parcels: number;
  nonFbaParcels: number;
  ratedParts: Record<Part, number>;
  ratedParcels: number;
  ratedNonFbaParcels: number;
  unratedMonths: string[];
  splitSuspectMonths: string[];
}

export type Block = "Fixed" | "Customer parcels" | "Amazon inbound" | "Other variable";
export const BLOCKS: readonly Block[] = ["Fixed", "Customer parcels", "Amazon inbound", "Other variable"];

export interface Drivers { d2c: number; fba: number }

export type RateKey = "d2c" | "fba" | "other";
export type RateGap = "not_used" | "no_count";

export interface Rates {
  d2c: number | null;
  fba: number | null;
  other: number | null;
  gaps: Partial<Record<RateKey, RateGap>>;
  combined: boolean;
}

/** A zero service-part amount means that service was not used; add-ons may be genuine zero rates. */
function rateOf(amount: number, count: number, zeroMeansUnused: boolean): [number | null, RateGap | null] {
  if (zeroMeansUnused && amount === 0) return [null, "not_used"];
  if (count > 0) return [amount / count, null];
  return amount === 0 ? [0, null] : [null, "no_count"];
}

export function rates(rate: RunRate, splitUnreliable = false): Rates {
  const gaps: Rates["gaps"] = {};
  const take = (key: RateKey, [value, gap]: [number | null, RateGap | null]): number | null => {
    if (gap) gaps[key] = gap;
    return value;
  };
  const rated = rate.ratedParts;
  const withObservedCost = (
    result: [number | null, RateGap | null],
    observedAmount: number,
    ratedCount: number,
  ): [number | null, RateGap | null] => observedAmount !== 0 && ratedCount === 0
    ? [null, "no_count"]
    : result;
  const other = take("other", withObservedCost(
    rateOf(rated["Stock freight"] + rated.Other, rate.ratedParcels, false),
    rate.parts["Stock freight"] + rate.parts.Other,
    rate.ratedParcels,
  ));
  if (splitUnreliable) {
    const [both, gap] = withObservedCost(
      rateOf(rated["D2C shipping"] + rated["Amazon inbound"], rate.ratedParcels, true),
      rate.parts["D2C shipping"] + rate.parts["Amazon inbound"],
      rate.ratedParcels,
    );
    if (gap) {
      gaps.d2c = gap;
      gaps.fba = gap;
    }
    return { d2c: both, fba: both, other, gaps, combined: true };
  }
  return {
    d2c: take("d2c", withObservedCost(rateOf(rated["D2C shipping"], rate.ratedNonFbaParcels, true), rate.parts["D2C shipping"], rate.ratedNonFbaParcels)),
    fba: take("fba", withObservedCost(rateOf(rated["Amazon inbound"], rate.ratedParcels - rate.ratedNonFbaParcels, true), rate.parts["Amazon inbound"], rate.ratedParcels - rate.ratedNonFbaParcels)),
    other,
    gaps,
    combined: false,
  };
}

/** J&J UK's selected-range parcel counts scaled to twelve billed months. */
export function defaultDrivers(jjUk: RunRate | undefined): Drivers | null {
  if (!jjUk || jjUk.monthsBilled === 0) return null;
  const scale = 12 / jjUk.monthsBilled;
  return {
    d2c: Math.round(jjUk.nonFbaParcels * scale),
    fba: Math.round((jjUk.parcels - jjUk.nonFbaParcels) * scale),
  };
}

export const validDrivers = (drivers: Drivers | null): drivers is Drivers =>
  drivers != null
  && Number.isFinite(drivers.d2c)
  && Number.isFinite(drivers.fba)
  && drivers.d2c >= 0
  && drivers.fba >= 0
  && drivers.d2c + drivers.fba > 0;

export interface Standardised {
  providerId: string;
  blocks: Record<Block, number>;
  total: number | null;
  perParcel: number | null;
  missing: Array<{ key: RateKey; gap: RateGap }>;
  complete: boolean;
  combined: boolean;
}

export interface Minimums {
  pickPack: number | null;
  invoice: number | null;
}

export type MinimumDefault = { amount: number; currency: "EUR" | "USD" };

/** Agreed monthly minimums are contracts of the 3PL, not properties of its bill currency. */
export function minimumDefault(id: string): MinimumDefault | null {
  if (id === "jj_uk") return { amount: 500, currency: "EUR" };
  if (id === "omnipack") return { amount: 2_500, currency: "EUR" };
  if (id === "jj_us") return { amount: 150, currency: "USD" };
  return null;
}

/** Apply monthly pick-and-pack and whole-invoice floors to a complete yearly comparison row. */
export function withMinimums(
  row: Standardised,
  d2cPerMonth: number,
  pickPackRate: number | null,
  min: Minimums,
): Standardised & { topUpPerYear: number; binding: boolean } {
  if (!row.complete || row.total == null) return { ...row, topUpPerYear: 0, binding: false };
  const pickPackAtVolume = pickPackRate == null ? null : pickPackRate * d2cPerMonth;
  const pickPackTopUp = min.pickPack == null || pickPackAtVolume == null
    ? 0
    : Math.max(0, min.pickPack - pickPackAtVolume);
  const beforeInvoiceMinimum = row.total / 12 + pickPackTopUp;
  const invoiceTopUp = min.invoice == null ? 0 : Math.max(0, min.invoice - beforeInvoiceMinimum);
  const topUpPerYear = (pickPackTopUp + invoiceTopUp) * 12;
  const total = row.total + topUpPerYear;
  const yearlyParcels = row.perParcel != null && row.perParcel !== 0 ? row.total / row.perParcel : null;
  return {
    ...row,
    total,
    perParcel: yearlyParcels == null || yearlyParcels === 0 ? row.perParcel : total / yearlyParcels,
    topUpPerYear,
    binding: topUpPerYear > 0,
  };
}

/** Invoice-currency to GBP ratio across billed totals in the selected range. */
export function invoiceToGbp(gbp: TplHistory, invoice: TplHistory, from: string, to: string): number | null {
  const totalInRange = (history: TplHistory): number => history.months
    .filter((month) => ym(month.month) >= from && ym(month.month) <= to)
    .reduce((sum, month) => sum + Number(month.total), 0);
  const gbpTotal = totalInRange(gbp);
  const invoiceTotal = totalInRange(invoice);
  return gbpTotal !== 0 && invoiceTotal !== 0 && Number.isFinite(gbpTotal) && Number.isFinite(invoiceTotal)
    ? gbpTotal / invoiceTotal
    : null;
}

export interface MinimumConversionSource {
  providerId: string;
  currency: string;
  gbp: TplHistory | undefined;
  invoice: TplHistory | undefined;
}

/** Resolve a contract minimum's currency through a 3PL whose bills serve that currency. */
export function minimumToGbpRatio(
  providerId: string,
  billCurrency: string,
  minimumCurrency: string,
  sources: MinimumConversionSource[],
  from: string,
  to: string,
): number | null {
  if (minimumCurrency === "GBP") return 1;
  const candidates = minimumCurrency === billCurrency
    ? sources.filter((source) => source.providerId === providerId)
    : sources.filter((source) => source.currency === minimumCurrency);
  for (const source of candidates) {
    if (!source.gbp || !source.invoice) continue;
    const ratio = invoiceToGbp(source.gbp, source.invoice, from, to);
    if (ratio != null) return ratio;
  }
  return null;
}

export function standardised(rate: RunRate, drivers: Drivers, splitUnreliable = false): Standardised {
  const providerRates = rates(rate, splitUnreliable);
  const missing: Standardised["missing"] = [];
  const term = (key: RateKey, volume: number): number => {
    if (volume === 0) return 0;
    const value = providerRates[key];
    if (value == null) {
      missing.push({ key, gap: providerRates.gaps[key] ?? "no_count" });
      return 0;
    }
    return value * volume;
  };
  if (rate.fixedPerMonth == null) {
    return {
      providerId: rate.providerId,
      blocks: { Fixed: 0, "Customer parcels": 0, "Amazon inbound": 0, "Other variable": 0 },
      total: null,
      perParcel: null,
      missing,
      complete: false,
      combined: providerRates.combined,
    };
  }
  const blocks: Record<Block, number> = {
    Fixed: rate.fixedPerMonth * 12,
    "Customer parcels": term("d2c", drivers.d2c),
    "Amazon inbound": term("fba", drivers.fba),
    "Other variable": term("other", drivers.d2c + drivers.fba),
  };
  const total = BLOCKS.reduce((sum, block) => sum + blocks[block], 0);
  return {
    providerId: rate.providerId,
    blocks,
    total,
    perParcel: total / (drivers.d2c + drivers.fba),
    missing,
    complete: missing.length === 0,
    combined: providerRates.combined,
  };
}

/** Rank comparable rows first; partial totals never establish the cheapest benchmark. */
export function rankStandardised<T extends Standardised>(rows: T[]): Array<T & { vsCheapest: number | null }> {
  const byTotal = (a: Standardised, b: Standardised) => a.total! - b.total!;
  const complete = rows.filter((row) => row.complete).sort(byTotal);
  const partial = rows.filter((row) => !row.complete && row.total != null).sort(byTotal);
  const min = complete[0]?.total ?? null;
  return [
    ...complete.map((row, index) => ({
      ...row,
      vsCheapest: index === 0 || min == null || min <= 0 ? null : row.total! / min - 1,
    })),
    ...partial.map((row) => ({ ...row, vsCheapest: null })),
    ...rows.filter((row) => row.total == null).map((row) => ({ ...row, vsCheapest: null })),
  ];
}

export interface Decision {
  cheapestId: string;
  dearestId: string;
  gapPerMonth: number;
  aboutSame: boolean;
  fixedShareOfGap: number | null;
  rows: Array<{
    providerId: string;
    workPerMonth: number;
    fixedPerMonth: number;
    totalPerMonth: number;
  }>;
}

type LegendLineArgs = {
  from?: string;
  to?: string;
  provider?: string;
  storagePerMonth?: number;
  parcelsPerMonth?: number;
  dearest?: string;
  cheapest?: string;
  gapPerMonth?: number;
  minimumLabel?: string;
};

const roundedLegendNumber = (value: number): string => Math.round(value).toLocaleString("en-GB");

/** Plain-language chart legends and the standardised saving line. */
export function legendLine(kind: "actual" | "standardised" | "index", args: LegendLineArgs = {}): string {
  if (kind === "index") return "100 = each 3PL's first month with parcels. Above 100 = dearer than then.";
  if (kind === "standardised") {
    if (!args.dearest || !args.cheapest || args.gapPerMonth == null) {
      return "Every 3PL is priced for the same work from its own bills.";
    }
    const minimum = args.minimumLabel ? ` ${args.minimumLabel}'s minimum applies.` : "";
    return `${args.dearest} would cost £${roundedLegendNumber(args.gapPerMonth)} a month more than ${args.cheapest} for the same work.${minimum}`;
  }
  const range = `As billed, ${args.from ?? ""} to ${args.to ?? ""}.`;
  if (!args.provider || args.storagePerMonth == null || args.parcelsPerMonth == null) return range;
  return `${range} ${args.provider}: £${roundedLegendNumber(args.storagePerMonth)} a month storage for ${roundedLegendNumber(args.parcelsPerMonth)} parcels.`;
}

/** Monthly decision from complete standardised rows (ranked cheapest first); null when fewer than two. */
export function decision(rows: Array<Standardised & { vsCheapest: number | null }>): Decision | null {
  const complete = rows.filter((row) => row.complete && row.total != null);
  if (complete.length < 2) return null;
  const cheapest = complete[0];
  const dearest = complete.at(-1)!;
  const gapPerMonth = (dearest.total! - cheapest.total!) / 12;
  const dearestPerMonth = dearest.total! / 12;
  const fixedGapPerMonth = (dearest.blocks.Fixed - cheapest.blocks.Fixed) / 12;
  return {
    cheapestId: cheapest.providerId,
    dearestId: dearest.providerId,
    gapPerMonth,
    aboutSame: dearestPerMonth === 0 ? gapPerMonth === 0 : gapPerMonth < dearestPerMonth * 0.01,
    fixedShareOfGap: gapPerMonth === 0 ? null : fixedGapPerMonth / gapPerMonth,
    rows: complete.map((row) => {
      const fixedPerMonth = row.blocks.Fixed / 12;
      const totalPerMonth = row.total! / 12;
      return {
        providerId: row.providerId,
        workPerMonth: totalPerMonth - fixedPerMonth,
        fixedPerMonth,
        totalPerMonth,
      };
    }),
  };
}

export const perParcel = (rate: RunRate): number | null => div(rate.total, rate.parcels);
export function actualMonthly(rate: RunRate): {
  billed: number;
  parcels: number;
  perParcel: number | null;
  storage: number;
  subscription: number;
} | null {
  if (rate.monthsBilled === 0) return null;
  return {
    billed: rate.total / rate.monthsBilled,
    parcels: rate.parcels / rate.monthsBilled,
    perParcel: div(rate.total, rate.parcels),
    storage: rate.parts.Storage / rate.monthsBilled,
    subscription: rate.parts.Subscription / rate.monthsBilled,
  };
}
export function actualPerParcel(rate: RunRate): (Record<Part, number> & { total: number }) | null {
  if (rate.parcels === 0) return null;
  return {
    ...Object.fromEntries(PARTS.map((part) => [part, rate.parts[part] / rate.parcels])) as Record<Part, number>,
    total: rate.total / rate.parcels,
  };
}
export const parcelsPerMonth = (rate: RunRate): number | null => div(rate.parcels, rate.monthsBilled);

/** Ids whose average parcels per billed month in From..To is below INDEX_FLOOR. */
export function belowIndexFloor(histories: TplHistory[], from: string, to: string): string[] {
  return histories.flatMap((history) => {
    const average = parcelsPerMonth(runRate(history, from, to));
    return average != null && average < INDEX_FLOOR ? [history.provider.provider_id] : [];
  });
}
export const variablePerParcel = (rate: RunRate): number | null => div(rate.variable + rate.other, rate.parcels);

const validOrders = (orders: number | null): orders is number => orders != null && Number.isFinite(orders) && orders > 0;

/** Fixed per month spread over `orders`, plus the variable cost per parcel. */
export function perParcelAt(rate: RunRate, orders: number | null): number | null {
  const variable = variablePerParcel(rate);
  if (!validOrders(orders) || rate.fixedPerMonth == null || variable == null) return null;
  return rate.fixedPerMonth / orders + variable;
}

export function yearlyAt(rate: RunRate, orders: number | null): number | null {
  const variable = variablePerParcel(rate);
  if (!validOrders(orders) || rate.fixedPerMonth == null || variable == null) return null;
  return (rate.fixedPerMonth + variable * orders) * 12;
}

/** Sum of costs over sum of parcels across 3PLs with parcels. */
export function pooledPerParcel(rates: RunRate[]): number | null {
  const withParcels = rates.filter((rate) => rate.parcels > 0);
  return div(
    withParcels.reduce((sum, rate) => sum + rate.total, 0),
    withParcels.reduce((sum, rate) => sum + rate.parcels, 0),
  );
}

export function runRate(history: TplHistory, from: string, to: string): RunRate {
  const id = history.provider.provider_id;
  const parts = emptyParts();
  const ratedParts = emptyParts();
  let monthsBilled = 0;
  let parcels = 0;
  let nonFbaParcels = 0;
  let ratedParcels = 0;
  let ratedNonFbaParcels = 0;
  const unratedMonths: string[] = [];
  const splitSuspectMonths: string[] = [];
  for (const month of history.months) {
    if (!inWindow(month, to, monthsBetween(from, to)) || !isBilled(id, month)) continue;
    monthsBilled += 1;
    const mp = monthParts(id, month);
    for (const part of PARTS) parts[part] += mp[part];
    parcels += month.parcels.total;
    nonFbaParcels += nonFba(month);
    if (month.parcels.total > 0) {
      for (const part of PARTS) ratedParts[part] += mp[part];
      ratedParcels += month.parcels.total;
      ratedNonFbaParcels += nonFba(month);
    } else {
      unratedMonths.push(ym(month.month));
    }
    if (isSuspect(month)) splitSuspectMonths.push(ym(month.month));
  }
  const total = sumParts(parts);
  const scale = monthsBilled === 0 ? null : 12 / monthsBilled;
  const fixed = parts.Subscription + parts.Storage;
  const variable = parts["D2C shipping"] + parts["Amazon inbound"] + parts["Stock freight"];
  const other = parts.Other;
  const yearlyParts = scale == null ? null : (Object.fromEntries(PARTS.map((part) => [part, parts[part] * scale])) as Record<Part, number>);
  return {
    providerId: id, monthsBilled, parts, total,
    yearly: scale == null ? null : total * scale, yearlyParts, fixed, variable, other,
    fixedShare: div(fixed, fixed + variable + Math.max(0, other)),
    fixedPerMonth: div(fixed, monthsBilled),
    parcels, nonFbaParcels, ratedParts, ratedParcels, ratedNonFbaParcels, unratedMonths, splitSuspectMonths,
  };
}

export interface TrendRow { month: string; [key: string]: number | string | null }

export function trendRows(histories: TplHistory[], from: string, to: string): TrendRow[] {
  const rows: TrendRow[] = [];
  for (let i = monthsBetween(from, to) - 1; i >= 0; i -= 1) {
    const month = addMonths(to, -i);
    const row: TrendRow = { month };
    for (const history of histories) {
      const id = history.provider.provider_id;
      const served = history.months.find((m) => ym(m.month) === month);
      const billed = served != null && isBilled(id, served);
      row[id] = billed ? Number(served.total) : null;
      row[`${id}_pp`] = billed && served.total_per_parcel != null ? Number(served.total_per_parcel) : null;
    }
    rows.push(row);
  }
  return rows;
}

type Itemised = "pick_pack" | "packaging" | "courier";
const ITEMISED: readonly Itemised[] = ["pick_pack", "packaging", "courier"];

export interface YearRow {
  year: number;
  label: string;
  loaded: "full" | "partial" | "none";
  monthsBilled: number;
  cost: number | null;
  parcels: number;
  costPerParcel: number | null;
  costPerParcelChange: number | null;
  perNonFba: Record<Itemised, number | null>;
  perNonFbaChange: Record<Itemised, number | null>;
  storageMonths: number;
  storagePerMonth: number | null;
  storagePerMonthChange: number | null;
  subscriptionMonths: number;
  subscriptionPerMonth: number | null;
  suspectMonths: string[];
  changeSuspect: boolean;
}

export function yearRows(history: TplHistory, to: string, loadedFrom: string, fromMonth = HISTORY_FROM): YearRow[] {
  const id = history.provider.provider_id;
  const lastYear = Number(to.slice(0, 4));
  const firstVisibleYear = Number(fromMonth.slice(0, 4));
  const rows: YearRow[] = [];
  for (let year = Number(HISTORY_FROM.slice(0, 4)); year <= lastYear; year += 1) {
    const startMonth = year === firstVisibleYear ? Number(fromMonth.slice(5, 7)) : 1;
    const start = `${year}-${String(startMonth).padStart(2, "0")}`;
    const months = history.months.filter((m) => ym(m.month).startsWith(`${year}-`)
      && ym(m.month) >= start && ym(m.month) <= to && isBilled(id, m));
    const cost = months.length === 0 ? null : months.reduce((acc, m) => acc + sumParts(monthParts(id, m)), 0);
    const parcels = months.reduce((acc, m) => acc + m.parcels.total, 0);
    const nonFbaTotal = months.reduce((acc, m) => acc + nonFba(m), 0);
    const perNonFba = Object.fromEntries(
      ITEMISED.map((t) => [t, months.length === 0 ? null : div(months.reduce((acc, m) => acc + amountOf(m, t), 0), nonFbaTotal)]),
    ) as Record<Itemised, number | null>;
    const storage = months.filter((m) => hasLine(m, "storage"));
    const subscription = months.filter((m) => hasLine(m, "subscription"));
    const endMonth = year === lastYear ? Number(to.slice(5, 7)) : 12;
    const first = start;
    const clipped = startMonth !== 1 || endMonth !== 12;
    rows.push({
      year,
      label: clipped ? `${year} (${MONTH_NAMES[startMonth - 1]} to ${MONTH_NAMES[endMonth - 1]})` : String(year),
      loaded: `${year}-12` < loadedFrom ? "none" : first < loadedFrom ? "partial" : "full",
      monthsBilled: months.length,
      cost,
      parcels,
      costPerParcel: cost == null ? null : div(cost, parcels),
      costPerParcelChange: null,
      perNonFba,
      perNonFbaChange: { pick_pack: null, packaging: null, courier: null },
      storageMonths: storage.length,
      storagePerMonth: storage.length === 0 ? null : storage.reduce((acc, m) => acc + amountOf(m, "storage"), 0) / storage.length,
      storagePerMonthChange: null,
      subscriptionMonths: subscription.length,
      subscriptionPerMonth: subscription.length === 0 ? null : subscription.reduce((acc, m) => acc + amountOf(m, "subscription"), 0) / subscription.length,
      suspectMonths: months.filter(isSuspect).map((m) => ym(m.month)),
      changeSuspect: false,
    });
  }
  rows.forEach((row, i) => {
    if (i === 0) return;
    const prev = rows[i - 1];
    row.costPerParcelChange = change(row.costPerParcel, prev.costPerParcel);
    row.storagePerMonthChange = change(row.storagePerMonth, prev.storagePerMonth);
    for (const t of ITEMISED) row.perNonFbaChange[t] = change(row.perNonFba[t], prev.perNonFba[t]);
    row.changeSuspect = row.suspectMonths.length > 0 || prev.suspectMonths.length > 0;
  });
  return rows.filter((row) => row.year >= firstVisibleYear);
}

export type PerParcelKey = "pick_pack" | "packaging" | "courier" | "d2c" | "d2c_amazon";
export type PerParcel = number | null | "not_itemised" | "unreliable";

export function perParcelByPart(history: TplHistory, from: string, to: string): Record<PerParcelKey, PerParcel> {
  const id = history.provider.provider_id;
  const months = history.months.filter((m) => inWindow(m, to, monthsBetween(from, to)) && isBilled(id, m) && nonFba(m) > 0);
  const ratedMonths = history.months.filter((m) => inWindow(m, to, monthsBetween(from, to)) && isBilled(id, m) && m.parcels.total > 0);
  const parcels = months.reduce((acc, m) => acc + nonFba(m), 0);
  const allParcels = ratedMonths.reduce((acc, m) => acc + m.parcels.total, 0);
  const negativePart = months.some((m) => ITEMISED.some((t) => amountOf(m, t) < 0));
  const fbaGap = months.filter((m) => m.flags.some((f) => f.code === "fba_gap")).length;
  const unreliable = months.length > 0 && (negativePart || fbaGap * 2 >= months.length);
  const unsplit = months.reduce((acc, m) => acc + amountOf(m, "fulfilment_unsplit"), 0);
  const d2c = months.reduce((acc, m) => acc + D2C_TYPES.reduce((a, t) => a + amountOf(m, t), 0), 0);
  const ratedD2cAmazon = ratedMonths.reduce((acc, m) => acc
    + D2C_TYPES.reduce((amount, type) => amount + amountOf(m, type), 0)
    + amountOf(m, "amazon_reshipping"), 0);
  const one = (t: Itemised): PerParcel => {
    if (unreliable) return "unreliable";
    const amount = months.reduce((acc, m) => acc + amountOf(m, t), 0);
    if (amount === 0 && unsplit !== 0) return "not_itemised";
    return div(amount, parcels);
  };
  return {
    pick_pack: one("pick_pack"),
    packaging: one("packaging"),
    courier: one("courier"),
    d2c: unreliable ? "unreliable" : div(d2c, parcels),
    d2c_amazon: div(ratedD2cAmazon, allParcels),
  };
}

export function moveTfiSaving(tfi: RunRate, receivingD2cPerParcel: PerParcel, extraPerYear: number | null): number | null {
  if (tfi.yearly == null || tfi.monthsBilled === 0 || typeof receivingD2cPerParcel !== "number") return null;
  if (extraPerYear == null || !Number.isFinite(extraPerYear) || extraPerYear < 0) return null;
  const parcelsPerYear = (tfi.nonFbaParcels * 12) / tfi.monthsBilled;
  return tfi.yearly - parcelsPerYear * receivingD2cPerParcel - extraPerYear;
}

export function agedStockSaving(jjUk: RunRate, agedPct: number | null): number | null {
  if (agedPct == null || !Number.isFinite(agedPct) || agedPct < 0 || agedPct > 100 || jjUk.yearlyParts == null) return null;
  return (jjUk.yearlyParts.Storage * agedPct) / 100;
}
